'use client';

import { CheckCircleIcon, XCircleIcon } from '@wolfejs/icons/IconMap';
import { useUpdatePassword } from '@wolfejs/ms-api-react/src/hooks';
import { Form } from '@wolfejs/ui/Form/Form';
import type { FormSchemaEntry } from '@wolfejs/ui/Form/types';
import { Title } from '@wolfejs/ui/Title';
import { UI } from '@wolfejs/ui/UI';
import type { UIComponentProps } from '@wolfejs/ui/types/UI';
import { cn } from '@wolfejs/ui/utils/classnames';
import { useEffect, useState } from 'react';
import { z } from 'zod';

const schema: FormSchemaEntry[] = [
  {
    label: 'Enter your current password',
    name: 'currentPassword',
    type: 'password',
    validation: z.string().min(2),
    showRequiredIndicator: true,
    props: {
      autoComplete: 'current-password',
    },
  },
  {
    label: 'Enter your new password',
    name: 'newPassword',
    type: 'password',
    validation: z.string().min(10),
    showRequiredIndicator: true,
    props: {
      autoComplete: 'newPassword',
    },
  },
  {
    label: 'Confirm new password',
    name: 'confirmedNewPassword',
    type: 'password',
    validation: z.string(),
    showRequiredIndicator: true,
    props: {
      autoComplete: 'confirmedNewPassword',
    },
  },
];

export type ResetPasswordFormValues = {
  currentPassword: string;
  newPassword: string;
  confirmedNewPassword: string;
};

export type AdminResetPasswordProps = UIComponentProps & {
  defaultValues?: Partial<ResetPasswordFormValues>;
};
export const AdminResetPasswordForm = ({ className, defaultValues }: AdminResetPasswordProps) => {
  const { mutateAsync: updatePassword, isLoading, error } = useUpdatePassword();
  const [values, setValues] = useState<typeof defaultValues>();
  const lowerCaseRule = /[a-z]/;
  const upperCaseRule = /[A-Z]/;
  const digitRule = /[\d]/;
  const specialCharRule = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[@#$%^&+=!?])[A-Za-z\d@#$%^&+=!?]{10,100}$/;
  const [lengthRuleApplied, setLengthRuleApplied] = useState<boolean>(false);
  const [samePasswordsRuleApplied, setSamePasswordsRuleApplied] = useState<boolean>(false);
  const [digitRuleApplied, setDigitRuleApplied] = useState<boolean>(false);
  const [lowercaseCharRuleApplied, setLowercaseCharRuleApplied] = useState<boolean>(false);
  const [uppercaseCharRuleApplied, setUppercaseCharRuleApplied] = useState<boolean>(false);
  const [specialCharRuleApplied, setSpecialCharRuleApplied] = useState<boolean>(false);

  useEffect(() => {
    if (!values) return;
    const { newPassword, confirmedNewPassword } = values;
    setLengthRuleApplied(!!newPassword?.length && newPassword?.length >= 10 && newPassword?.length <= 100);
    setLowercaseCharRuleApplied(!!newPassword && lowerCaseRule.test(newPassword));
    setUppercaseCharRuleApplied(!!newPassword && upperCaseRule.test(newPassword));
    setDigitRuleApplied(!!newPassword && digitRule.test(newPassword));
    setSpecialCharRuleApplied(!!newPassword && specialCharRule.test(newPassword));
    setSamePasswordsRuleApplied(!!newPassword && !!confirmedNewPassword && newPassword === confirmedNewPassword);
  }, [values]);

  const handleSubmit = async (formValues: Partial<ResetPasswordFormValues>) => {
    const { currentPassword, newPassword, confirmedNewPassword } = formValues;
    if (!currentPassword || !confirmedNewPassword || !newPassword) return;
    try {
      await updatePassword({
        oldPassword: currentPassword,
        newPassword: newPassword,
        newPasswordConfirm: confirmedNewPassword,
      });
      UI.notifications.success('Your password has been successfully updated.');
      setValues({
        currentPassword: '',
        newPassword: '',
        confirmedNewPassword: '',
      });
    } catch (error) {
      UI.notifications.error(`There was an error during password update process. ${error}`);
      return;
    }
  };

  return (
    <>
      <Title as="h3">Password</Title>
      <div className={cn('@container grid grid-cols-2 gap-4 lg:grid-cols-2', className)}>
        <Form<Partial<ResetPasswordFormValues>>
          schema={schema}
          onSubmit={handleSubmit}
          onChange={setValues}
          values={values}
          hasSubmitButton
          error={error}
          submitButtonProps={{
            disabled: !(
              lengthRuleApplied &&
              digitRuleApplied &&
              samePasswordsRuleApplied &&
              lowercaseCharRuleApplied &&
              uppercaseCharRuleApplied &&
              specialCharRuleApplied
            ),
            loading: isLoading,
          }}
          submitButtonText="Save changes"
          fieldsetLayoutClasses="grid grid-cols-1 gap-4"
        />
        <div className="bg-default-50 h-fit rounded-lg p-5 text-sm font-medium">
          <div>Password requirements:</div>
          <div className="text-default-500 mb-4">Ensure that these requirements are met:</div>
          <ul className="text-default-500">
            <li className="flex items-center gap-1.5">
              {samePasswordsRuleApplied ? <CheckCircleIcon/> : <XCircleIcon/>} Confirmed password and new password
              are the same
            </li>
            <li className="flex items-center gap-1.5">
              {lengthRuleApplied ? <CheckCircleIcon/> : <XCircleIcon/>} At least 10 characters (and up to 100
              characters)
            </li>
            <li className="flex items-center gap-1.5">
              {lowercaseCharRuleApplied ? <CheckCircleIcon/> : <XCircleIcon/>} At least one lowercase character
            </li>
            <li className="flex items-center gap-1.5">
              {uppercaseCharRuleApplied ? <CheckCircleIcon/> : <XCircleIcon/>} At least one uppercase character
            </li>
            <li className="flex items-center gap-1.5">
              {digitRuleApplied ? <CheckCircleIcon/> : <XCircleIcon/>} At least one digit
            </li>
            <li className="flex items-center gap-1.5">
              {specialCharRuleApplied ? <CheckCircleIcon/> : <XCircleIcon/>} At least one special
              character (e.g., @ # $ % ^ & + = ! ?) and no periods (.) or underscores (_) allowed
            </li>
          </ul>
        </div>
      </div>
    </>
  );
};
